import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ref, get, remove } from 'firebase/database';
import SEO from '../components/SEO';

const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

interface TableMeta { name: string; columns: string[]; rowCount: number; }
interface Dataset {
  id: string;
  name: string;
  description: string;
  format: 'txt' | 'word' | 'csv' | 'excel' | 'sql';
  status: 'ready' | 'processing' | 'error';
  errorMessage?: string;
  sizeBytes: number;
  textLength?: number;
  tables?: TableMeta[];
  createdAt: number;
  updatedAt: number;
}

const FORMAT_ICONS: Record<string, string> = { txt: '📄', word: '📝', csv: '📊', excel: '📊', sql: '🗄️' };
const FORMAT_LABELS: Record<string, string> = { txt: 'Text', word: 'Word', csv: 'CSV', excel: 'Excel', sql: 'SQL dump' };
const STRUCTURED = new Set(['csv', 'excel', 'sql']);
const ACCEPTED_EXTENSIONS = '.txt,.docx,.csv,.xls,.xlsx,.sql';

const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '20px', marginBottom: '14px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGhost: { padding: '6px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
};

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Datasets() {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewTables, setPreviewTables] = useState<Array<{ name: string; columns: string[]; sample: string[][] }>>([]);
  const [previewType, setPreviewType] = useState<'text' | 'structured'>('text');
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uid = auth.currentUser?.uid;

  const loadDatasets = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const snap = await get(ref(db, `datasets/${uid}`));
      const list: Dataset[] = [];
      if (snap.exists()) {
        snap.forEach(c => { list.push({ id: c.key!, ...c.val() }); });
        list.sort((a, b) => b.createdAt - a.createdAt);
      }
      setDatasets(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDatasets(); }, [uid]);

  const openUpload = () => {
    setFile(null); setName(''); setDescription(''); setUploadError('');
    setShowUpload(true);
  };

  const handleFileChosen = (f: File | null) => {
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const doUpload = async () => {
    if (!file) { setUploadError('Choose a file first.'); return; }
    setUploading(true);
    setUploadError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const form = new FormData();
      form.append('file', file);
      form.append('name', name || file.name);
      form.append('description', description);
      const r = await fetch(`${API_BASE}/api/datasets/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Upload failed');
      setShowUpload(false);
      await loadDatasets();
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete dataset "${name}"? Any bots using it will lose access to this knowledge.`)) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const r = await fetch(`${API_BASE}/api/datasets/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setDatasets(ds => ds.filter(d => d.id !== id));
      if (previewId === id) setPreviewId(null);
    } catch (e: any) {
      alert(e.message || 'Delete failed');
    }
  };

  const openPreview = async (id: string) => {
    setPreviewId(id);
    setPreviewLoading(true);
    setPreviewText('');
    setPreviewTables([]);
    try {
      const token = await auth.currentUser?.getIdToken();
      const r = await fetch(`${API_BASE}/api/datasets/${id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Failed to load preview');
      if (data.type === 'structured') {
        setPreviewType('structured');
        setPreviewTables(data.tables || []);
      } else {
        setPreviewType('text');
        setPreviewText(data.preview + (data.totalLength > (data.preview?.length || 0) ? '\n\n… (truncated)' : ''));
      }
    } catch (e: any) {
      setPreviewType('text');
      setPreviewText('Could not load preview: ' + (e.message || 'unknown error'));
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
      Loading datasets...
    </div>
  );

  return (
    <>
      <SEO title="Datasets" description="Manage knowledge base datasets for your AI bots" type="website" noindex />
      <div style={S.page}>
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <button onClick={() => navigate('/dashboard')} style={{ ...S.btnGhost, marginBottom: '12px' }}>← Dashboard</button>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: '700', margin: 0 }}>📦 Datasets</h1>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '4px 0 0' }}>Upload knowledge bases your AI bots can answer from — pick one when you create or edit a bot.</p>
            </div>
            <button onClick={openUpload} style={S.btnPrimary}>+ Upload dataset</button>
          </div>

          <div style={{ ...S.card, background: '#5B4FE810', border: '1px solid #5B4FE830', marginBottom: '20px' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6' }}>
              💡 <strong>The more organized your data, the better your bot's answers.</strong> For CSV/Excel/SQL: use clear column headers — your bot filters rows by what the customer asks about. For text files: use ALL-CAPS section headings so the bot picks the right section.
              {' '}Supported formats: <strong>.txt, .docx, .csv, .xls, .xlsx, .sql</strong> (INSERT-style dump).
            </div>
          </div>

          {datasets.length === 0 ? (
            <div style={{ ...S.card, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>📦</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '6px' }}>No datasets yet</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>Upload your first dataset to start building an AI bot.</p>
              <button onClick={openUpload} style={S.btnPrimary}>+ Upload dataset</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
              {datasets.map(d => (
                <div key={d.id} style={{ ...S.card, marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{FORMAT_ICONS[d.format] || '📄'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{FORMAT_LABELS[d.format] || d.format} · {fmtBytes(d.sizeBytes)}</div>
                    </div>
                  </div>
                  {d.description && <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: '0 0 10px' }}>{d.description}</p>}
                  {STRUCTURED.has(d.format) && d.tables?.length ? (
                    <div style={{ marginBottom: '10px' }}>
                      {d.tables.map(t => (
                        <div key={t.name} style={{ fontSize: '11px', color: 'var(--text-faint)', marginBottom: '2px' }}>
                          🗂 <code style={{ color: '#818cf8' }}>{t.name}</code> — {t.rowCount.toLocaleString()} rows · {t.columns.length} cols
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openPreview(d.id)} style={{ ...S.btnGhost, flex: 1 }}>👁 View</button>
                    <button onClick={() => doDelete(d.id, d.name)} style={S.btnDanger}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !uploading && setShowUpload(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '28px', maxWidth: '440px', width: '90%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700', margin: '0 0 16px' }}>Upload dataset</h2>

            <div style={{ marginBottom: '14px' }}>
              <label style={S.label}>File (.txt, .docx, .csv, .xls, .xlsx, .sql)</label>
              <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS}
                onChange={e => handleFileChosen(e.target.files?.[0] || null)} style={S.input} />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={S.label}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Pricing & FAQ" style={S.input} />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={S.label}>Description <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What's in this dataset?" style={S.input} />
            </div>
            {uploadError && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '14px' }}>{uploadError}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowUpload(false)} disabled={uploading} style={S.btnGhost}>Cancel</button>
              <button onClick={doUpload} disabled={uploading || !file} style={{ ...S.btnPrimary, opacity: uploading || !file ? 0.6 : 1 }}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setPreviewId(null)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '24px', maxWidth: '860px', width: '95%', maxHeight: '85vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '700', margin: 0 }}>Dataset preview</h2>
              <button onClick={() => setPreviewId(null)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>
            {previewLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
            ) : previewType === 'structured' ? (
              previewTables.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No tables found.</div>
              ) : (
                previewTables.map(t => (
                  <div key={t.name} style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', color: '#818cf8', fontFamily: 'monospace', marginBottom: '8px', fontWeight: 600 }}>
                      🗂 {t.name} — first {t.sample.length} rows shown
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-page)' }}>
                            {t.columns.map(col => (
                              <th key={col} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-faint)', fontWeight: 600, borderBottom: '1px solid var(--bg-element)', whiteSpace: 'nowrap' }}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {t.sample.map((row, ri) => (
                            <tr key={ri} style={{ borderBottom: '1px solid var(--bg-element)' }}>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{ padding: '5px 10px', color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {cell == null ? <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>NULL</span> : String(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )
            ) : (
              <pre style={{ background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', padding: '14px 16px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0 }}>{previewText}</pre>
            )}
          </div>
        </div>
      )}
    </>
  );
}
